import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Stack
} from '@mui/material';
import { zoullooColors } from '../../theme';
import {
  Send as SendIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  PersonSearch as PersonSearchIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ProposalWorkflowAction, FreelancerProposalStatus } from '../../models/ProposalWorkflow';
import { useAvailableActions, useExecuteWorkflowAction } from '../../hooks/useProposalWorkflow';
import DeclineReasonSelector from '../DeclineReasonSelector';
import CastingSlotsInput from '../CastingSlotsInput';
import RevisionRequestInput from '../RevisionRequestInput';
import ClientActionSelector from '../ClientActionSelector';
import FreelancerResponseInput from '../FreelancerResponseInput';
import { FreelancerDeclineReason } from '../../models/enums/FreelancerDeclineReason';
import { ClientDeclineReason } from '../../models/enums/ClientDeclineReason';
import { ClientProposalAction } from '../../models/enums/ClientProposalAction';
import { useAuthProvider } from '../../auth/hooks/auth-provider';
import { UserType } from '../../models/UserType';

// Statuses where the CLIENT must act (client has the ball)
// When CSM is in these statuses, they should see the client action dropdown
// Note: CastingToSchedule is NOT included because in that status, the CSM must act (schedule the casting)
// CastingScheduled IS included because after the casting, the client must decide (accept/refuse/request revision)
const CLIENT_ACTION_STATUSES: FreelancerProposalStatus[] = [
  FreelancerProposalStatus.SubmittedClient,
  FreelancerProposalStatus.RevisedForClient,
  FreelancerProposalStatus.InDiscussionWithClient,
  FreelancerProposalStatus.CastingScheduled  // After casting, client must decide
  // CastingToSchedule is excluded - CSM must schedule the casting
];

interface ProposalWorkflowActionsProps {
  proposalId: number;
  currentStatus: FreelancerProposalStatus;
  onActionComplete?: () => void;
  hasUnsavedChanges?: boolean;
  onBeforeAction?: () => Promise<boolean>;
}

const ProposalWorkflowActions: React.FC<ProposalWorkflowActionsProps> = ({
  proposalId,
  currentStatus,
  onActionComplete,
  hasUnsavedChanges = false,
  onBeforeAction
}) => {
  const { t } = useTranslation();
  const { user } = useAuthProvider();
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ProposalWorkflowAction | null>(null);
  const [reason, setReason] = useState('');

  // New state for enhanced workflow
  const [freelancerDeclineReason, setFreelancerDeclineReason] = useState<number | null>(null);
  const [freelancerDeclineReasonDetails, setFreelancerDeclineReasonDetails] = useState('');
  const [clientDeclineReason, setClientDeclineReason] = useState<number | null>(null);
  const [clientDeclineReasonDetails, setClientDeclineReasonDetails] = useState('');
  const [castingSlots, setCastingSlots] = useState('');
  const [revisionMessage, setRevisionMessage] = useState('');
  const [freelancerResponseMessage, setFreelancerResponseMessage] = useState('');

  // Client-specific state
  const [clientAction, setClientAction] = useState<ClientProposalAction | null>(null);

  // Local execution state to show loading immediately (before onBeforeAction and mutation)
  const [isExecuting, setIsExecuting] = useState(false);

  // State for inline revision input (for CSM/Client)
  const [showRevisionInput, setShowRevisionInput] = useState(false);

  const { data: availableActions, isLoading: isLoadingActions } = useAvailableActions(proposalId);
  const executeAction = useExecuteWorkflowAction();

  // Check if user is a client
  const isClient = user?.type === UserType.Client;

  // Check if user is a CSM (Internal)
  const isCSM = user?.type === UserType.Internal;

  // Check if CSM is acting on behalf of client (proposal is in a status where client should act)
  const isCSMActingForClient = isCSM && CLIENT_ACTION_STATUSES.includes(currentStatus);

  const handleActionClick = async (action: ProposalWorkflowAction) => {
    // Special handling for RequestRevision - show inline input instead of dialog
    if (action === ProposalWorkflowAction.RequestRevision) {
      setShowRevisionInput(true);
      return;
    }

    // Some actions require a reason
    if (requiresReason(action)) {
      setSelectedAction(action);
      setReasonDialogOpen(true);
    } else {
      await executeActionHandler(action);
    }
  };

  const requiresReason = (action: ProposalWorkflowAction): boolean => {
    return [
      ProposalWorkflowAction.RefuseByClient,
      ProposalWorkflowAction.RefuseByTalent,
      ProposalWorkflowAction.Cancel,
      ProposalWorkflowAction.RequestRevision,
      ProposalWorkflowAction.Revise,
      ProposalWorkflowAction.Submit
    ].includes(action);
  };

  const executeActionHandler = async (action: ProposalWorkflowAction, actionReason?: string) => {
    // Start loading immediately (before onBeforeAction)
    setIsExecuting(true);

    try {
      // Call onBeforeAction if provided (e.g., to save form changes)
      if (onBeforeAction) {
        const canProceed = await onBeforeAction();
        if (!canProceed) {
          console.error('❌ onBeforeAction returned false, aborting workflow action');
          setIsExecuting(false);
          return;
        }
      }

      // Determine which message to send based on the action
      let messageToSend = revisionMessage || freelancerResponseMessage || undefined;

      // For casting requests, use casting slots as the message
      if (action === ProposalWorkflowAction.RequestCasting) {
        messageToSend = castingSlots;
      }

      const payload = {
        proposalId,
        action,
        reason: actionReason || reason,
        message: messageToSend,
        freelancerDeclineReason: freelancerDeclineReason || undefined,
        freelancerDeclineReasonDetails: freelancerDeclineReasonDetails || undefined,
        clientDeclineReason: clientDeclineReason || undefined,
        clientDeclineReasonDetails: clientDeclineReasonDetails || undefined,
        castingProposedSlots: castingSlots || undefined
      };

      executeAction.mutate(
        payload,
        {
          onSuccess: () => {
            setIsExecuting(false);
            resetDialogState();
            onActionComplete?.();
          },
          onError: () => {
            setIsExecuting(false);
          }
        }
      );
    } catch (error) {
      console.error('Error in executeActionHandler:', error);
      setIsExecuting(false);
    }
  };

  const resetDialogState = () => {
    setReasonDialogOpen(false);
    setReason('');
    setSelectedAction(null);
    setFreelancerDeclineReason(null);
    setFreelancerDeclineReasonDetails('');
    setClientDeclineReason(null);
    setClientDeclineReasonDetails('');
    setCastingSlots('');
    setRevisionMessage('');
    setFreelancerResponseMessage('');
    setClientAction(null);
    setIsExecuting(false); // Reset loading state when dialog closes
    setShowRevisionInput(false); // Reset inline revision input
  };

  const mapClientActionToWorkflowAction = (action: ClientProposalAction): ProposalWorkflowAction => {
    switch (action) {
      case ClientProposalAction.RequestCasting:
        return ProposalWorkflowAction.RequestCasting;
      case ClientProposalAction.RequestRevision:
        return ProposalWorkflowAction.RequestRevision;
      case ClientProposalAction.AcceptProposal:
        return ProposalWorkflowAction.Accept;
      case ClientProposalAction.RefuseProposal:
        return ProposalWorkflowAction.RefuseByClient;
      default:
        throw new Error(`Unknown client action: ${action}`);
    }
  };

  const handleClientActionSubmit = async () => {
    if (!clientAction) return;

    const workflowAction = mapClientActionToWorkflowAction(clientAction);
    await executeActionHandler(workflowAction);
  };

  const handleReasonSubmit = async () => {
    if (selectedAction) {
      await executeActionHandler(selectedAction);
    }
  };

  const handleInlineRevisionSubmit = async () => {
    await executeActionHandler(ProposalWorkflowAction.RequestRevision);
  };

  const handleInlineRevisionCancel = () => {
    setShowRevisionInput(false);
    setRevisionMessage('');
  };

  const isSubmitDisabled = () => {
    if (!selectedAction) return true;

    switch (selectedAction) {
      case ProposalWorkflowAction.RefuseByTalent:
        return !freelancerDeclineReason ||
               (freelancerDeclineReason === FreelancerDeclineReason.Other && !freelancerDeclineReasonDetails.trim());

      case ProposalWorkflowAction.RefuseByClient:
        return !clientDeclineReason ||
               (clientDeclineReason === ClientDeclineReason.Other && !clientDeclineReasonDetails.trim());

      case ProposalWorkflowAction.RequestCasting:
        return !castingSlots.trim();

      case ProposalWorkflowAction.RequestRevision:
        return !revisionMessage.trim();

      case ProposalWorkflowAction.Revise:
      case ProposalWorkflowAction.Submit:
        // Message is optional but if provided, must not be empty
        return false;

      case ProposalWorkflowAction.Cancel:
        return !reason.trim();

      default:
        return false;
    }
  };

  const getActionIcon = (action: ProposalWorkflowAction) => {
    switch (action) {
      case ProposalWorkflowAction.Submit:
        return <SendIcon />;
      case ProposalWorkflowAction.TransmitToClient:
        return <SendIcon />;
      case ProposalWorkflowAction.Accept:
        return <CheckIcon />;
      case ProposalWorkflowAction.RefuseByClient:
      case ProposalWorkflowAction.RefuseByTalent:
        return <ClearIcon />;
      case ProposalWorkflowAction.RequestRevision:
      case ProposalWorkflowAction.Revise:
        return <EditIcon />;
      case ProposalWorkflowAction.Cancel:
        return <CancelIcon />;
      case ProposalWorkflowAction.ScheduleCasting:
        return <ScheduleIcon />;
      case ProposalWorkflowAction.RequestCasting:
        return <PersonSearchIcon />;
      default:
        return null;
    }
  };

  const getActionLabel = (action: ProposalWorkflowAction) => {
    // Textes personnalisés pour les actions principales
    if (action === ProposalWorkflowAction.RequestRevision) {
      return t('proposal.workflow.actionLabels.requestRevision');
    }
    if (action === ProposalWorkflowAction.TransmitToClient) {
      return t('proposal.workflow.actionLabels.transmitToClient');
    }
    if (action === ProposalWorkflowAction.Revise) {
      return t('proposal.workflow.actionLabels.revise');
    }
    if (action === ProposalWorkflowAction.Submit) {
      return t('proposal.workflow.actionLabels.submit');
    }
    return t(`proposal.actions.${action}`);
  };

  const getStatusColor = (status: FreelancerProposalStatus): string => {
    switch (status) {
      case FreelancerProposalStatus.NotSubmitted:
        return zoullooColors.grey[450]; // Gris moyen
      case FreelancerProposalStatus.SubmittedCSM:
        return zoullooColors.secondary.main; // Persian Blue #4836bf
      case FreelancerProposalStatus.SubmittedClient:
        return zoullooColors.secondary.dark; // Midnight Blue #201E69
      case FreelancerProposalStatus.InDiscussionWithCSM:
        return zoullooColors.primary.main; // Coquelicot #FF6300
      case FreelancerProposalStatus.InDiscussionWithClient:
        return zoullooColors.primary.light; // Orange clair #FF8F70
      case FreelancerProposalStatus.RevisedForCSM:
        return zoullooColors.proposal.gunmetal; // Bleu-gris #242C35
      case FreelancerProposalStatus.RevisedForClient:
        return zoullooColors.proposal.dyke; // Brun #3B2325
      case FreelancerProposalStatus.CastingToSchedule:
        return '#6B5CE5'; // Violet (nuance du Persian Blue)
      case FreelancerProposalStatus.CastingScheduled:
        return '#5B4AC9'; // Violet plus foncé
      case FreelancerProposalStatus.Accepted:
        return zoullooColors.success.main; // Vert #4CAF50
      case FreelancerProposalStatus.RefusedTalent:
        return zoullooColors.error.main; // Rouge #F44336
      case FreelancerProposalStatus.RefusedClient:
        return zoullooColors.error.dark; // Rouge foncé #D32F2F
      case FreelancerProposalStatus.Cancelled:
        return zoullooColors.grey[900]; // Gris foncé #2A2A2A
      default:
        return zoullooColors.grey[450];
    }
  };

  // Filter actions based on unsaved changes for InDiscussion and Revised statuses
  // This filtering ONLY applies to Freelancers editing their own proposal
  const getFilteredActions = (): ProposalWorkflowAction[] => {
    if (!availableActions) return [];

    // Check if user is a freelancer
    const isFreelancer = user?.type === UserType.Freelancer;

    // HIDE RefuseByTalent action for freelancers AFTER they submit the proposal
    // Allow it ONLY in NotSubmitted (draft) status so they can decline before creating/submitting
    let actions = isFreelancer && currentStatus !== FreelancerProposalStatus.NotSubmitted
      ? availableActions.filter(action => action !== ProposalWorkflowAction.RefuseByTalent)
      : availableActions;

    // For freelancers in NotSubmitted status (draft), hide Submit action
    // because the "Send proposal" button at the bottom of the form already handles this
    if (isFreelancer && currentStatus === FreelancerProposalStatus.NotSubmitted) {
      const filtered = actions.filter(action =>
        action !== ProposalWorkflowAction.Submit
      );
      return filtered;
    }

    // Check if we're in a revision workflow (InDiscussion or Revised statuses)
    const isInRevisionWorkflow =
      currentStatus === FreelancerProposalStatus.InDiscussionWithCSM ||
      currentStatus === FreelancerProposalStatus.InDiscussionWithClient ||
      currentStatus === FreelancerProposalStatus.RevisedForCSM ||
      currentStatus === FreelancerProposalStatus.RevisedForClient;

    // Only apply filtering for freelancers in revision workflow
    if (!isInRevisionWorkflow || !isFreelancer) {
      return actions;
    }

    // In revision workflow: filter actions based on whether freelancer has made changes
    if (hasUnsavedChanges) {
      // If freelancer modified the form, only allow Revise
      const filtered = actions.filter(action =>
        action === ProposalWorkflowAction.Revise
      );
      return filtered;
    } else {
      // If freelancer didn't modify, only allow Submit (without changes)
      const filtered = actions.filter(action =>
        action === ProposalWorkflowAction.Submit
      );
      return filtered;
    }
  };

  const filteredActions = getFilteredActions();

  if (isLoadingActions) {
    return (
      <Box display="flex" alignItems="center" gap={2}>
        <CircularProgress size={20} />
        <Typography variant="body2">{t('loading')}</Typography>
      </Box>
    );
  }

  if (!filteredActions || filteredActions.length === 0) {
    return (
      <Box>
        <Chip
          label={t(`proposal.status.${currentStatus}`)}
          size="small"
          variant="outlined"
          sx={{
            borderColor: getStatusColor(currentStatus),
            color: zoullooColors.text.primary,
            fontWeight: 500
          }}
        />
      </Box>
    );
  }

  // Client-specific UI (for real clients)
  if (isClient) {
    return (
      <Box>
        <Stack direction="column" spacing={2}>
          <ClientActionSelector
            selectedAction={clientAction}
            onActionChange={setClientAction}
            availableActions={filteredActions}
            castingSlots={castingSlots}
            onCastingSlotsChange={setCastingSlots}
            revisionMessage={revisionMessage}
            onRevisionMessageChange={setRevisionMessage}
            declineReason={clientDeclineReason}
            declineReasonDetails={clientDeclineReasonDetails}
            onDeclineReasonChange={setClientDeclineReason}
            onDeclineReasonDetailsChange={setClientDeclineReasonDetails}
            onSubmit={handleClientActionSubmit}
            isSubmitting={isExecuting || executeAction.isPending}
          />
        </Stack>
      </Box>
    );
  }

  // CSM acting on behalf of client - show dropdown with special title
  if (isCSMActingForClient && filteredActions.length > 0) {
    return (
      <Box>
        <Stack direction="column" spacing={2}>
          <Box sx={{
            p: 3,
            backgroundColor: 'rgba(255, 99, 0, 0.08)',
            borderRadius: 2,
            border: `2px solid ${zoullooColors.warning.main}`,
            mt: 2
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: zoullooColors.warning.dark }}>
              ⚠️ {t('proposal.workflow.pendingClientActions')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: zoullooColors.text.secondary }}>
              {t('proposal.workflow.csmActingForClientInfo')}
            </Typography>
            <ClientActionSelector
              selectedAction={clientAction}
              onActionChange={setClientAction}
              availableActions={filteredActions}
              castingSlots={castingSlots}
              onCastingSlotsChange={setCastingSlots}
              revisionMessage={revisionMessage}
              onRevisionMessageChange={setRevisionMessage}
              declineReason={clientDeclineReason}
              declineReasonDetails={clientDeclineReasonDetails}
              onDeclineReasonChange={setClientDeclineReason}
              onDeclineReasonDetailsChange={setClientDeclineReasonDetails}
              onSubmit={handleClientActionSubmit}
              isSubmitting={isExecuting || executeAction.isPending}
            />
          </Box>
        </Stack>
      </Box>
    );
  }

  // Default UI for CSM/Admin/Freelancer
  return (
    <>
      <Box>
        <Stack direction="column" spacing={2}>
          {filteredActions && filteredActions.length > 0 && (
            <Box sx={{
              p: 3,
              backgroundColor: 'rgba(255, 99, 0, 0.08)',
              borderRadius: 2,
              border: `2px solid ${zoullooColors.warning.main}`,
              mt: 2
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: zoullooColors.warning.dark }}>
                ⚠️ {t('proposal.workflow.pendingActions')}
              </Typography>
              {user?.type === UserType.Freelancer && (
                <Typography variant="body2" sx={{ mb: 3, color: zoullooColors.text.secondary, whiteSpace: 'pre-line' }}>
                  {t('proposal.workflow.freelancerInstructions')}
                </Typography>
              )}
              <Box
                display="flex"
                justifyContent="center"
                gap={3}
                flexWrap="wrap"
              >
                {filteredActions.map((action) => (
                  <Button
                    key={action}
                    onClick={() => handleActionClick(action)}
                    startIcon={isExecuting || executeAction.isPending ? <CircularProgress size={20} /> : getActionIcon(action)}
                    disabled={isExecuting || executeAction.isPending}
                    variant="outlined"
                    size="medium"
                    sx={{
                      borderColor: zoullooColors.secondary.main,
                      color: zoullooColors.secondary.main,
                      minWidth: '250px',
                      py: 1.5,
                      '&:hover': {
                        borderColor: zoullooColors.secondary.dark,
                        backgroundColor: zoullooColors.secondary.alpha,
                      },
                      '&.Mui-disabled': {
                        borderColor: zoullooColors.border.light,
                        color: zoullooColors.text.disabled,
                      }
                    }}
                  >
                    {getActionLabel(action)}
                  </Button>
                ))}
              </Box>
            </Box>
          )}

          {/* Inline Revision Request Input */}
          {showRevisionInput && (
            <Box sx={{ mt: 3, p: 3, backgroundColor: zoullooColors.grey[25], borderRadius: 2, border: `1px solid ${zoullooColors.border.light}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Proposer une modification au talent
              </Typography>
              <RevisionRequestInput
                value={revisionMessage}
                onChange={setRevisionMessage}
                userType="csm"
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button
                  onClick={handleInlineRevisionCancel}
                  disabled={isExecuting || executeAction.isPending}
                  variant="outlined"
                  sx={{
                    borderColor: zoullooColors.grey[450],
                    color: zoullooColors.grey[450],
                    '&:hover': {
                      borderColor: zoullooColors.grey[600],
                      backgroundColor: zoullooColors.grey[50]
                    }
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleInlineRevisionSubmit}
                  disabled={!revisionMessage.trim() || isExecuting || executeAction.isPending}
                  variant="contained"
                  startIcon={isExecuting || executeAction.isPending ? <CircularProgress size={20} /> : <SendIcon />}
                  sx={{
                    backgroundColor: zoullooColors.primary.main,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: zoullooColors.primary.dark
                    },
                    '&.Mui-disabled': {
                      backgroundColor: zoullooColors.grey[150],
                      color: zoullooColors.text.disabled
                    }
                  }}
                >
                  {t('common.send')}
                </Button>
              </Box>
            </Box>
          )}
        </Stack>
      </Box>

      {/* Reason Dialog */}
      <Dialog
        open={reasonDialogOpen}
        onClose={() => {
          if (!isExecuting && !executeAction.isPending) {
            resetDialogState();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedAction === ProposalWorkflowAction.Revise || selectedAction === ProposalWorkflowAction.Submit
            ? t('proposal.workflow.reviseDialog.title')
            : selectedAction && t(`proposal.actions.${selectedAction}`)}
        </DialogTitle>
        <DialogContent>
          {selectedAction === ProposalWorkflowAction.RefuseByTalent && (
            <DeclineReasonSelector
              type="freelancer"
              reason={freelancerDeclineReason}
              reasonDetails={freelancerDeclineReasonDetails}
              onReasonChange={setFreelancerDeclineReason}
              onReasonDetailsChange={setFreelancerDeclineReasonDetails}
            />
          )}

          {selectedAction === ProposalWorkflowAction.RefuseByClient && (
            <DeclineReasonSelector
              type="client"
              reason={clientDeclineReason}
              reasonDetails={clientDeclineReasonDetails}
              onReasonChange={setClientDeclineReason}
              onReasonDetailsChange={setClientDeclineReasonDetails}
            />
          )}

          {selectedAction === ProposalWorkflowAction.RequestCasting && (
            <CastingSlotsInput
              value={castingSlots}
              onChange={setCastingSlots}
            />
          )}

          {selectedAction === ProposalWorkflowAction.RequestRevision && (
            <RevisionRequestInput
              value={revisionMessage}
              onChange={setRevisionMessage}
              userType="csm"
            />
          )}

          {selectedAction === ProposalWorkflowAction.Revise && (
            <FreelancerResponseInput
              value={freelancerResponseMessage}
              onChange={setFreelancerResponseMessage}
              actionType="revise"
            />
          )}

          {selectedAction === ProposalWorkflowAction.Submit && (
            <FreelancerResponseInput
              value={freelancerResponseMessage}
              onChange={setFreelancerResponseMessage}
              actionType="submit"
            />
          )}

          {selectedAction === ProposalWorkflowAction.Cancel && (
            <>
              <Typography variant="body2" gutterBottom>
                {t('proposal.reasonRequired')}
              </Typography>
              <TextField
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                placeholder={t('proposal.enterReason')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                margin="normal"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDialogState} disabled={isExecuting || executeAction.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleReasonSubmit}
            color="primary"
            variant="contained"
            disabled={isSubmitDisabled() || isExecuting || executeAction.isPending}
          >
            {(isExecuting || executeAction.isPending) ? <CircularProgress size={20} /> : (
              selectedAction === ProposalWorkflowAction.Revise || selectedAction === ProposalWorkflowAction.Submit
                ? t('proposal.workflow.reviseDialog.confirm')
                : t('common.confirm')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProposalWorkflowActions;